import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const http = axios.create({
  baseURL: API,
  withCredentials: true,
});

// --- Silent refresh + friendly error interceptor ---
// On 401, try POST /auth/refresh once. If it succeeds, retry original request.
let refreshing = null;
http.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    if (status !== 401) return Promise.reject(error);
    if (original._retried) return Promise.reject(error);
    if (url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/register")) {
      return Promise.reject(error);
    }
    original._retried = true;
    try {
      refreshing = refreshing || http.post("/auth/refresh").finally(() => { refreshing = null; });
      await refreshing;
      return http(original);
    } catch {
      return Promise.reject(error);
    }
  }
);

// Convenience: extract friendly detail from an Axios error
export function errMessage(error, fallback = "Ralat / Error") {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").filter(Boolean).join(" ") || fallback;
  if (d?.msg) return d.msg;
  return fallback;
}

export async function sendChat(session_id, text, context_hint, persona, config = {}) {
  const { data } = await http.post(
    "/chat",
    { session_id, text, context_hint, persona },
    config
  );
  return data;
}

export async function getChat(session_id) {
  const { data } = await http.get(`/chat/${session_id}`);
  return data;
}

export async function transcribeVoice(blob) {
  const form = new FormData();
  form.append("file", blob, "voice.webm");
  const { data } = await http.post("/voice/transcribe", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function extractUpload(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await http.post("/upload/extract", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function saveTrip(session_id, title, trip) {
  const { data } = await http.post("/trips", { session_id, title, trip });
  return data;
}

export async function listTrips() {
  const { data } = await http.get("/trips");
  return data;
}

export async function getTrip(trip_id) {
  const { data } = await http.get(`/trips/${trip_id}`);
  return data;
}

export async function deleteTrip(trip_id) {
  const { data } = await http.delete(`/trips/${trip_id}`);
  return data;
}

export async function translateTrip(trip, target_lang) {
  const { data } = await http.post("/trips/translate", { trip, target_lang });
  return data;
}

// --- Stripe (web only) ---
export async function stripeFetchPrice(currency) {
  const q = currency ? `?currency=${encodeURIComponent(currency)}` : "";
  const { data } = await http.get(`/payments/stripe/price${q}`);
  return data;
}
export async function stripeCreateCheckout(originUrl, currency) {
  const { data } = await http.post("/payments/stripe/create-checkout-session", { origin_url: originUrl, currency });
  return data;
}
export async function stripeCheckStatus(sessionId) {
  const { data } = await http.get(`/payments/stripe/status/${sessionId}`);
  return data;
}
export async function fetchEntitlement() {
  const { data } = await http.get("/payments/entitlement");
  return data;
}

// --- Support / Purchase Restoration ---
export async function submitRestorationTicket(body) {
  const { data } = await http.post("/support/purchase-restore", body);
  return data;
}
export async function listRestorationTickets() {
  const { data } = await http.get("/support/tickets");
  return data;
}

export async function fetchMe() {
  const { data } = await http.get("/auth/me");
  return data;
}

export async function postAuthSession(session_id) {
  const { data } = await http.post("/auth/session", null, {
    headers: { "X-Session-ID": session_id },
  });
  return data;
}

export async function logout() {
  const { data } = await http.post("/auth/logout");
  return data;
}

export async function forgotPassword(email, lang) {
  const { data } = await http.post("/auth/forgot-password", { email }, { headers: lang ? { "X-Lang": lang } : {} });
  return data;
}

export async function resetPassword(token, password) {
  const { data } = await http.post("/auth/reset-password", { token, password });
  return data;
}

export async function patchMe(update) {
  const { data } = await http.patch("/users/me", update);
  return data;
}

// -------- Admin analytics --------

export async function adminOverview(days = 30) {
  const { data } = await http.get(`/admin/overview?days=${days}`);
  return data;
}

export async function adminTimeseries(days = 30) {
  const { data } = await http.get(`/admin/analytics/timeseries?days=${days}`);
  return data;
}

export async function adminPersonas(days = 30) {
  const { data } = await http.get(`/admin/analytics/personas?days=${days}`);
  return data;
}

export async function adminDestinations(days = 30) {
  const { data } = await http.get(`/admin/analytics/destinations?days=${days}`);
  return data;
}

export async function adminLanguage(days = 30) {
  const { data } = await http.get(`/admin/analytics/language?days=${days}`);
  return data;
}

